import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Login() {
  const [guestLoading, setGuestLoading] = useState(false);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGuestLogin = async () => {
    setGuestLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/guest`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Guest login failed');
      const data = await res.json();
      localStorage.setItem('session_token', data.session_token);
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Guest login error:', err);
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      {/* Header with notebook theme */}
      <div
        className="relative overflow-hidden py-10 sm:py-16 px-4 sm:px-6 lg:px-8"
        style={{
          background: 'linear-gradient(to bottom, #f5f1e8 0%, #ede7d9 100%)',
          borderBottom: '2px solid #d4c5a9',
        }}
      >
        {/* Spiral binding on left */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-12 hidden sm:block"
          style={{
            background: 'linear-gradient(to right, #8B7355 0%, #A0826D 50%, #8B7355 100%)',
            boxShadow: 'inset -2px 0 4px rgba(0,0,0,0.2)',
          }}
        >
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute left-1/2 transform -translate-x-1/2"
              style={{
                top: `${i * 5 + 2.5}%`,
                width: '8px',
                height: '8px',
                background: 'radial-gradient(circle, #3d3d3d 40%, transparent 40%)',
                borderRadius: '50%',
              }}
            />
          ))}
        </div>

        {/* Horizontal notebook lines */}
        <div className="absolute inset-0 pointer-events-none opacity-30 hidden sm:block">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute left-0 right-0 border-b"
              style={{
                top: `${i * 6.67}%`,
                borderColor: '#d4c5a9',
                marginLeft: '60px',
              }}
            />
          ))}
        </div>

        {/* Main content */}
        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-center justify-center">
            {/* Left column - Text content */}
            <div className="text-center lg:text-left relative z-10 lg:pl-24">
              <div className="text-center mb-6 sm:mb-8 relative z-20">
                <h1
                  className="text-3xl sm:text-5xl tracking-tighter font-semibold lg:font-medium text-stone-900 mb-3 sm:mb-4"
                  style={{ 
                    fontFamily: 'Manrope, sans-serif',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  }}
                  data-testid="login-title"
                >
                  Task Splitter
                  <br />
                  <span className="text-[#E07A5F]">
                    Conquer
                    <br />
                    Analysis&nbsp;Paralysis
                  </span>
                </h1>
                <p
                  className="text-lg sm:text-2xl font-semibold lg:font-medium text-stone-800"
                  style={{ 
                    fontFamily: 'Manrope, sans-serif',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  }}
                >
                  Your Procrastination Popper
                </p>
              </div>
              
              <div
                className="text-sm sm:text-lg text-stone-800 mb-6 sm:mb-8 relative z-20 font-medium"
                style={{ 
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  textShadow: '0 1px 2px rgba(0,0,0,0.08)',
                }}
              >
                <ul className="space-y-2 sm:space-y-3 inline-block text-left">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-[#E07A5F] text-xl sm:text-2xl flex-shrink-0">•</span>
                    <span>Break overwhelming tasks into bite-sized&nbsp;steps</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-[#E07A5F] text-xl sm:text-2xl flex-shrink-0">•</span>
                    <span>AI-powered breakdown when your brain won't&nbsp;cooperate</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-[#E07A5F] text-xl sm:text-2xl flex-shrink-0">•</span>
                    <span>Pomodoro timer with mood-matching&nbsp;themes</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-[#E07A5F] text-xl sm:text-2xl flex-shrink-0">•</span>
                    <span>Celebrate every small win, not just the finish&nbsp;line</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right column - Crumpled paper image (hidden on small mobile, visible on sm+) */}
            <div className="hidden sm:flex items-center justify-center lg:justify-start relative lg:-ml-32">
              <img 
                src="https://customer-assets.emergentagent.com/job_task-breakdown-17/artifacts/c663f9at_6_20260405_101800_0005.png"
                alt="Crushing overwhelming task lists"
                className="w-80 sm:w-96 lg:w-[30rem] object-contain lg:relative absolute lg:opacity-100 opacity-40 pointer-events-none"
                style={{ 
                  filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.15))',
                  transform: 'rotate(-8deg)',
                  right: '-5%',
                  bottom: '10%',
                  zIndex: 1,
                }}
              />
            </div>
          </div>

          {/* Auth buttons centered */}
          <div className="text-center mt-6 sm:mt-8 space-y-3">
            <button
              onClick={handleLogin}
              data-testid="google-login-btn"
              className="bg-[#E07A5F] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-medium hover:bg-[#D46B4E] shadow-sm inline-flex items-center gap-3 text-base sm:text-lg"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
            <div>
              <button
                onClick={handleGuestLogin}
                disabled={guestLoading}
                data-testid="guest-login-btn"
                className="text-stone-600 hover:text-stone-900 px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-medium border border-stone-300 hover:border-stone-400 shadow-sm inline-flex items-center gap-2 text-sm sm:text-base transition-colors disabled:opacity-50"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {guestLoading ? 'Setting up...' : 'Try as Guest'}
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Features section */}
      <div className="flex-1 py-10 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                title: 'AI-Powered Breakdown',
                description: 'Enter any task and let AI intelligently break it down into categories and actionable steps.',
                image: 'https://customer-assets.emergentagent.com/job_task-breakdown-17/artifacts/ddqoys1i_8_20260405_110150_0000.png',
                alt: 'AI-assisted project breakdown with organized checklist',
                position: 'center 70%',
                zoom: 'scale(2)',
              },
              {
                title: 'Hierarchical Organization',
                description: 'Organize tasks with categories and subtasks. See the full picture while tracking each detail.',
                image: 'https://customer-assets.emergentagent.com/job_task-breakdown-17/artifacts/vsw4q0gy_Screenshot_20260405_105419_Google.jpg',
                alt: 'Organized task board showing hierarchical structure',
                position: 'center center',
                zoom: 'scale(1)',
              },
              {
                title: 'Progress Tracking',
                description: 'Visual progress indicators show you exactly how far you\'ve come. Every step counts.',
                image: 'https://customer-assets.emergentagent.com/job_task-breakdown-17/artifacts/vjj5cew3_10_20260405_110234_0002.png',
                alt: 'Progress chart on tablet showing completion percentages',
                position: 'center 70%',
                zoom: 'scale(2)',
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                className="bg-white border border-stone-200 rounded-xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`feature-card-${idx}`}
              >
                <div 
                  className="mb-4 rounded-lg overflow-hidden"
                  style={{ width: '100%', height: '220px' }}
                >
                  <img 
                    src={feature.image} 
                    alt={feature.alt}
                    className="w-full h-full rounded-lg"
                    style={{ objectFit: 'cover', objectPosition: feature.position, transform: feature.zoom }}
                  />
                </div>
                <h3
                  className="text-lg sm:text-xl font-medium text-stone-900 mb-2 sm:mb-3"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {feature.title}
                </h3>
                <p
                  className="text-sm sm:text-base leading-relaxed text-stone-600"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Example task breakdown - 3 columns on desktop */}
          <div className="mt-10 sm:mt-16 bg-white border border-stone-200 rounded-xl p-5 sm:p-8 shadow-sm max-w-5xl mx-auto">
            <p
              className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-stone-500 mb-6 sm:mb-8 text-center"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Example: "Clean the Bathroom"
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {/* Column 1: Toilet */}
              <div>
                <h4 className="font-medium text-stone-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <div className="w-6 h-6 rounded-full border-2 border-[#81B29A] bg-[#81B29A] flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  Toilet
                </h4>
                <div className="space-y-3 ml-9">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#81B29A] border-2 border-[#81B29A] flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-stone-400 line-through" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      Scrub bowl
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#81B29A] border-2 border-[#81B29A] flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-stone-400 line-through" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      Clean seat and lid
                    </span>
                  </div>
                </div>
              </div>

              {/* Column 2: Sink & Mirror */}
              <div>
                <h4 className="font-medium text-stone-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <div className="w-6 h-6 rounded-full border-2 border-[#81B29A] bg-[#81B29A] flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  Sink & Mirror
                </h4>
                <div className="space-y-3 ml-9">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#81B29A] border-2 border-[#81B29A] flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-stone-400 line-through" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      Wipe down sink
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#81B29A] border-2 border-[#81B29A] flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-stone-400 line-through" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      Clean mirror
                    </span>
                  </div>
                </div>
              </div>

              {/* Column 3: Tub/Shower */}
              <div>
                <h4 className="font-medium text-stone-900 mb-4 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <div className="w-6 h-6 rounded-full border-2 border-stone-300 bg-white flex-shrink-0"></div>
                  Tub/Shower
                </h4>
                <div className="space-y-3 ml-9">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-stone-300 bg-white flex-shrink-0"></div>
                    <span className="text-sm text-stone-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      Scrub tub
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-stone-300 bg-white flex-shrink-0"></div>
                    <span className="text-sm text-stone-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      Replace shower curtain
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;